import {defineRecipe} from '@chakra-ui/react3'

const chakraButtonRecipe = defineRecipe({
    className: 'chakra-button',
    base: {
        display: 'inline-flex',
        appearance: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        position: 'relative',
        borderRadius: 'l2',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        borderWidth: '1px',
        borderColor: 'transparent',
        cursor: 'button',
        flexShrink: '0',
        outline: '0',
        lineHeight: '1.2',
        isolation: 'isolate',
        fontWeight: 'medium',
        transitionProperty: 'common',
        transitionDuration: 'moderate',
        focusVisibleRing: 'outside',
        _disabled: {
            layerStyle: 'disabled'
        },
        _icon: {
            flexShrink: '0'
        }
    },

    variants: {
        size: {
            '2xs': {
                h: '6',
                minW: '6',
                textStyle: 'xs',
                px: '2',
                gap: '1',
                _icon: {
                    width: '3.5',
                    height: '3.5'
                }
            },
            xs: {
                h: '8',
                minW: '8',
                textStyle: 'xs',
                px: '2.5',
                gap: '1',
                _icon: {
                    width: '4',
                    height: '4'
                }
            },
            sm: {
                h: '9',
                minW: '9',
                px: '3.5',
                textStyle: 'sm',
                gap: '2',
                _icon: {
                    width: '4',
                    height: '4'
                }
            },
            md: {
                h: '10',
                minW: '10',
                textStyle: 'sm',
                px: '4',
                gap: '2',
                _icon: {
                    width: '5',
                    height: '5'
                }
            },
            lg: {
                h: '11',
                minW: '11',
                textStyle: 'md',
                px: '5',
                gap: '3',
                _icon: {
                    width: '5',
                    height: '5'
                }
            },
            xl: {
                h: '12',
                minW: '12',
                textStyle: 'md',
                px: '5',
                gap: '2.5',
                _icon: {
                    width: '5',
                    height: '5'
                }
            },
            '2xl': {
                h: '16',
                minW: '16',
                textStyle: 'lg',
                px: '7',
                gap: '3',
                _icon: {
                    width: '6',
                    height: '6'
                }
            }
        },

        variant: {
            solid: {
                bg: 'colorPalette.solid',
                color: 'colorPalette.contrast',
                borderColor: 'transparent',
                _hover: {
                    bg: 'colorPalette.solid/90'
                },
                _expanded: {
                    bg: 'colorPalette.solid/90'
                }
            },

            subtle: {
                bg: 'colorPalette.subtle',
                color: 'colorPalette.fg',
                borderColor: 'transparent',
                _hover: {
                    bg: 'colorPalette.muted'
                },
                _expanded: {
                    bg: 'colorPalette.muted'
                }
            },

            surface: {
                bg: 'colorPalette.subtle',
                color: 'colorPalette.fg',
                shadow: '0 0 0px 1px var(--shadow-color)',
                shadowColor: 'colorPalette.muted',
                _hover: {
                    bg: 'colorPalette.muted'
                },
                _expanded: {
                    bg: 'colorPalette.muted'
                }
            },

            outline: {
                borderWidth: '1px',
                borderColor: 'colorPalette.muted',
                color: 'colorPalette.fg',
                _hover: {
                    bg: 'colorPalette.subtle'
                },
                _expanded: {
                    bg: 'colorPalette.subtle'
                }
            },

            ghost: {
                bg: 'transparent',
                color: 'colorPalette.fg',
                _hover: {
                    bg: 'colorPalette.subtle'
                },
                _expanded: {
                    bg: 'colorPalette.subtle'
                }
            },

            plain: {
                color: 'colorPalette.fg'
            }
        }
    },

    defaultVariants: {
        size: 'md',
        variant: 'solid'
    }
})

const mdBase = {
    paddingTop: 'spacing-8',
    paddingBottom: 'spacing-8',
    paddingLeft: 'spacing-16',
    paddingRight: 'spacing-16',
    // remind that text must be wrapped in span
    fontSize: 0, // hide unwrapped text
    '&>*': {textStyle: 'heading.small'}, // apply text style only on children
    gap: '1.5',
    _icon: {
        width: '5',
        height: '5'
    }
}

const secondaryFilledBase = {
    color: 'text.primary-inverted',
    _icon: {color: 'icons.primary-inverted'},
    _hover: {bg: 'background.actions.neutral-hover'},
    _focusVisible: {
        outlineColor: 'border.strong'
    }
}

export const buttonRecipe = defineRecipe({
    ...chakraButtonRecipe,
    // prevents chakra2 focus indicator from applying
    className: 'leap-button',
    base: {
        ...chakraButtonRecipe.base,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: '2',
        borderWidth: 0,
        borderRadius: 'round',
        fontWeight: null,
        lineHeight: null,
        transitionProperty: 'common',
        transitionDuration: 'normal',
        appearance: 'none',
        whiteSpace: 'normal',
        _focusVisible: {
            outlineOffset: '2px',
            outlineStyle: 'solid',
            outlineWidth: '1px'
        },
        _disabled: {
            opacity: 0.4,
            cursor: 'not-allowed'
        }
    },
    variants: {
        variant: {
            ...chakraButtonRecipe.variants.variant,
            primaryFilled: {
                bg: 'background.actions.primary',
                color: 'text.on-media.primary',
                _icon: {color: 'icons.on-color'},
                _hover: {bg: 'background.actions.primary-hover'},
                _focusVisible: {
                    outlineColor: 'border.brand'
                }
            },
            primaryOutline: {
                bg: 'transparent',
                color: 'text.brand-default',
                _icon: {color: 'icons.brand'},
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.brand',
                _hover: {
                    bg: 'background.actions.primary',
                    color: 'text.on-surface',
                    _icon: {color: 'icons.on-color'}
                },
                _focusVisible: {
                    outlineColor: 'border.brand'
                }
            },
            secondaryFilled: {
                ...secondaryFilledBase,
                bg: 'background.actions.secondary',
                _hover: {
                    bg: 'background.actions.secondary-hover'
                }
            },
            secondaryOutline: {
                bg: 'transparent',
                color: 'text.primary',
                _icon: {color: 'icons.primary'},
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.medium',
                _hover: {
                    bg: 'background.actions.secondary-hover',
                    color: 'text.primary-inverted',
                    _icon: {color: 'icons.primary-inverted'}
                },
                _focusVisible: {
                    outlineColor: 'border.strong',
                    bg: 'background.actions.secondary',
                    color: 'text.primary-inverted',
                    _icon: {color: 'icons.primary-inverted'}
                }
            },
            secondaryOnMedia: {
                bg: 'transparent',
                color: 'text.on-media.primary',
                _icon: {color: 'icons.on-colors'},
                borderWidth: '1px',
                borderStyle: 'solid',
                // not found color token for this could be added and reused
                borderColor: 'rgba(252, 252, 252, 1)',
                _hover: {
                    bg: 'rgba(252, 252, 252, 1)',
                    // not found color token for this could be added and reused
                    color: 'rgba(15, 15, 15, 1)',
                    _icon: {color: 'icons.on-color-inverted'}
                },
                _focusVisible: {
                    outlineColor: 'rgba(252, 252, 252, 1)'
                }
            },
            // Same as secondaryGhost with on-media text color for dark backgrounds
            secondaryGhostOnMedia: {
                bg: 'background.actions.on-media.ghost',
                color: 'text.on-media.primary',
                iconColor: 'icons.on-colors',
                _hover: {
                    bg: 'background.actions.on-media.ghost-hover'
                },
                _focusVisible: {
                    outlineColor: 'rgba(252, 252, 252, 1)'
                }
            },
            secondaryGhost: {
                bg: 'background.actions.ghost',
                color: 'text.primary',
                _icon: {color: 'icons.primary'},
                _hover: {
                    bg: 'background.actions.ghost-hover'
                },
                _focusVisible: {
                    outlineColor: 'border.strong',
                    bg: 'background.actions.secondary',
                    color: 'text.primary-inverted',
                    _icon: {color: 'icons.primary-inverted'}
                }
            },
            successFilled: {
                bg: 'background.system.success-strong',
                color: 'text.on-surface',
                _icon: {color: 'icons.on-color'},
                _hover: {
                    bg: 'background.system.success-strong-hover'
                },
                _focusVisible: {
                    outlineColor: 'border.system.success'
                }
            },
            successOutline: {
                bg: 'transparent',
                color: 'text.system.success',
                _icon: {color: 'icons.system.success'},
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.system.success',
                _hover: {
                    bg: 'background.system.success-strong',
                    color: 'text.on-surface',
                    _icon: {color: 'icons.on-color'}
                },
                _focusVisible: {
                    outlineColor: 'border.system.success'
                }
            },
            successGhost: {
                bg: 'background.system.success-light',
                color: 'text.system.success',
                _icon: {color: 'icons.system.success'},
                _hover: {
                    bg: 'background.system.success-subtle'
                },
                _focusVisible: {
                    outlineColor: 'border.system.success'
                }
            },
            errorFilled: {
                bg: 'background.system.error-strong',
                color: 'text.on-surface',
                _icon: {color: 'icons.on-color'},
                _hover: {
                    bg: 'background.system.error-strong-hover'
                },
                _focusVisible: {
                    outlineColor: 'border.system.error'
                }
            },
            errorOutline: {
                bg: 'transparent',
                color: 'text.system.error',
                _icon: {color: 'icons.system.error'},
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.system.error',
                _hover: {
                    bg: 'background.system.error-strong',
                    color: 'text.on-surface',
                    _icon: {color: 'icons.on-color'}
                },
                _focusVisible: {
                    outlineColor: 'border.system.error'
                }
            },
            errorGhost: {
                bg: 'background.system.error-light',
                color: 'text.system.error',
                _icon: {color: 'icons.system.error'},
                _hover: {
                    bg: 'background.system.error-subtle'
                },
                _focusVisible: {
                    outlineColor: 'border.system.error'
                }
            },
            mediaFilled: {
                bg: 'primary.base.white',
                _icon: {color: 'icons.on-color-inverted'},
                _focusVisible: {
                    outlineColor: 'primary.base.white'
                }
            },
            mediaGhost: {
                bg: 'background.actions.on-media.ghost',
                backdropFilter: 'blur(4px)',
                _icon: {color: 'icons.on-color'},
                _hover: {
                    bg: 'background.actions.on-media.ghost-hover'
                },
                _focusVisible: {
                    bg: 'background.actions.on-media.ghost-hover',
                    backdropFilter: 'none',
                    outlineColor: 'primary.base.white'
                },
                _disabled: {
                    bg: 'background.actions.on-media.ghost'
                }
            },
            babylistFilled: {
                // not in style definiions
                ...secondaryFilledBase,
                bg: 'partner.baby-list'
            }
        },
        width: {
            hugContent: {
                width: 'fit-content'
            },
            fillContent: {
                width: '100%'
            }
        },
        size: {
            sm: {
                paddingTop: 'spacing-4',
                paddingBottom: 'spacing-4',
                paddingLeft: 'spacing-12',
                paddingRight: 'spacing-12',
                // remind that text must be wrapped in span
                fontSize: 0, // hide unwrapped text
                '&>*': {textStyle: 'heading.small'}, // apply text style only on children
                gap: 'spacing-4',
                _icon: {
                    width: '4',
                    height: '4'
                },
                _hasLeftIcon: {paddingLeft: 'spacing-8'},
                _hasRightIcon: {paddingRight: 'spacing-8'},
                _hasOnlyIcon: {
                    paddingLeft: 'spacing-6',
                    paddingRight: 'spacing-6',
                    paddingTop: 'spacing-6',
                    paddingBottom: 'spacing-6'
                }
            },
            md: {
                ...mdBase,
                // IMPORTANT: When modifying _hasLeftIcon/_hasRightIcon/_hasOnlyIcon,
                // also update the corresponding md-... size variants below
                _hasLeftIcon: {paddingLeft: 'spacing-12'},
                _hasRightIcon: {paddingRight: 'spacing-12'},
                _hasOnlyIcon: {
                    paddingLeft: 'spacing-8',
                    paddingRight: 'spacing-8'
                }
            },
            // These size variants handle the limitation of CSS not distinguishing hidden elements.
            // Composite variants are not used as they would prevent responsive size changes.
            'md-icon-only': {
                ...mdBase,
                paddingLeft: 'spacing-8',
                paddingRight: 'spacing-8'
            },
            'md-left-icon': {
                ...mdBase,
                paddingLeft: 'spacing-12'
            },
            'md-right-icon': {
                ...mdBase,
                paddingRight: 'spacing-12'
            },
            lg: {
                paddingTop: 'spacing-10',
                paddingBottom: 'spacing-10',
                paddingLeft: 'spacing-20',
                paddingRight: 'spacing-20',
                // remind that text must be wrapped in span
                fontSize: 0, // hide unwrapped text
                '&>*': {textStyle: 'heading.medium'}, // apply text style only on children
                gap: '2',
                _icon: {
                    width: '6',
                    height: '6'
                },
                _hasLeftIcon: {paddingLeft: 'spacing-16'},
                _hasRightIcon: {paddingRight: 'spacing-16'},
                _hasOnlyIcon: {
                    paddingLeft: 'spacing-10',
                    paddingRight: 'spacing-10',
                    paddingTop: 'spacing-10',
                    paddingBottom: 'spacing-10'
                }
            }
        }
    },
    defaultVariants: {
        size: 'md',
        variant: 'primaryFilled',
        width: 'hugContent'
    }
})
